import type { ResponseGetMerchflowsCategories } from "@CommonApi/merchflows/categories";
import type { ParamsGetMerchflowsReviews } from "@CommonApi/merchflows/reviews";
import { type MerchflowsMultiValue, normalizeMultiValue } from "src/merchflows/shared/multiValue";

export type MerchflowsSearchParamsInput = {
  [key: string]: string | number | boolean | undefined;
};

type MerchflowsReviewFilterQueryParams = Pick<
  ParamsGetMerchflowsReviews,
  | "id"
  | "name"
  | "status"
  | "category"
  | "subcategory"
  | "goLiveDateFrom"
  | "goLiveDateTo"
  | "dateCreatedFrom"
  | "dateCreatedTo"
  | "dateModifiedFrom"
  | "dateModifiedTo"
  | "deactivationDateFrom"
  | "deactivationDateTo"
>;

export const parseCsvSearchParam = <T extends string>(value: string | undefined) => {
  if (!value) {
    return undefined;
  }

  return normalizeMultiValue<T>(value.split(",") as T[]);
};

export const serializeCsvSearchParam = <T extends string>(values: MerchflowsMultiValue<T>) => {
  const normalizedValues = normalizeMultiValue(values);
  return normalizedValues ? normalizedValues.join(",") : undefined;
};

export const getAvailableSubcategoryOptions = (
  availableCategories: ResponseGetMerchflowsCategories,
  categories: readonly string[] | undefined,
) => {
  const subcategories = new Set<string>();

  if (!categories?.length) {
    Object.values(availableCategories).forEach((categorySubcategories) => {
      categorySubcategories.forEach((subcategory) => subcategories.add(subcategory));
    });
  } else {
    categories.forEach((category) => {
      (availableCategories[category] || []).forEach((subcategory) =>
        subcategories.add(subcategory),
      );
    });
  }

  return Array.from(subcategories).sort((a, b) => a.localeCompare(b));
};

export const filterMultiSelectValues = <T extends string>(
  values: MerchflowsMultiValue<T>,
  allowedValues: readonly string[],
) => {
  const normalizedValues = normalizeMultiValue(values);
  if (!normalizedValues?.length) {
    return undefined;
  }

  const allowedValuesSet = new Set(allowedValues);
  const filteredValues = normalizedValues.filter((value) => allowedValuesSet.has(value));

  return filteredValues.length > 0 ? filteredValues : undefined;
};

export const buildMerchflowsFilterSearchParams = ({
  currentSearchParams,
  filterQueryParams,
  availableCategories,
  canValidateSubcategories,
}: {
  currentSearchParams: MerchflowsSearchParamsInput;
  filterQueryParams: MerchflowsReviewFilterQueryParams;
  availableCategories: ResponseGetMerchflowsCategories;
  canValidateSubcategories: boolean;
}): MerchflowsSearchParamsInput => {
  const normalizedStatus = normalizeMultiValue(filterQueryParams.status);
  const normalizedCategory = normalizeMultiValue(filterQueryParams.category);
  const normalizedSubcategory = normalizeMultiValue(filterQueryParams.subcategory);
  const nextSubcategories = canValidateSubcategories
    ? filterMultiSelectValues(
        normalizedSubcategory,
        getAvailableSubcategoryOptions(availableCategories, normalizedCategory),
      )
    : normalizedSubcategory;

  return {
    ...currentSearchParams,
    page: 1,
    id: filterQueryParams.id,
    name: filterQueryParams.name,
    status: serializeCsvSearchParam(normalizedStatus),
    category: serializeCsvSearchParam(normalizedCategory),
    subcategory: serializeCsvSearchParam(nextSubcategories),
    goLiveDateFrom: filterQueryParams.goLiveDateFrom,
    goLiveDateTo: filterQueryParams.goLiveDateTo,
    dateCreatedFrom: filterQueryParams.dateCreatedFrom,
    dateCreatedTo: filterQueryParams.dateCreatedTo,
    dateModifiedFrom: filterQueryParams.dateModifiedFrom,
    dateModifiedTo: filterQueryParams.dateModifiedTo,
    deactivationDateFrom: filterQueryParams.deactivationDateFrom,
    deactivationDateTo: filterQueryParams.deactivationDateTo,
  };
};
